import React from 'react';
import { FaChevronLeft, FaChevronRight, FaAngleDoubleLeft, FaAngleDoubleRight } from 'react-icons/fa';
import LoadingComponent from './Loading';

const Pagination = ({
    currentPage,
    totalPages,
    onPageChange,
    totalItems,
    itemsPerPage,
    itemName = 'عنصر',
    loading = false
}) => {
    // Calculate range of items being displayed
    const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalItems);

    // Generate page numbers with smart ellipsis
    const getPageNumbers = () => {
        const pages = [];
        const maxVisible = 7; // Maximum number of page buttons to show

        if (totalPages <= maxVisible) {
            // Show all pages if total is less than max
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            // Always show first page
            pages.push(1);

            if (currentPage <= 3) {
                // Near the beginning
                for (let i = 2; i <= 5; i++) {
                    pages.push(i);
                }
                pages.push('...');
                pages.push(totalPages);
            } else if (currentPage >= totalPages - 2) {
                // Near the end
                pages.push('...');
                for (let i = totalPages - 4; i <= totalPages; i++) {
                    pages.push(i);
                }
            } else {
                // In the middle
                pages.push('...');
                for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                    pages.push(i);
                }
                pages.push('...');
                pages.push(totalPages);
            }
        }

        return pages;
    };

    const pageNumbers = getPageNumbers();

    const handlePageClick = (page) => {
        if (page !== '...' && page !== currentPage && !loading) {
            onPageChange(page);
        }
    };

    return (
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 mt-6 font-cairo">
            {/* Items info */}
            <div className="flex items-center gap-4">
                <div className="text-sm text-gray-500">
                    عرض {startItem}-{endItem} من {totalItems} {itemName}
                </div>
                {loading && (
                    <div className="flex items-center gap-2 animate-fadeIn">
                        <LoadingComponent type="dots" size="sm" text="جاري التحديث..." color="main1" />
                    </div>
                )}
            </div>

            {/* Pagination controls */}
            <div className="flex items-center gap-2">
                {/* First page button */}
                <button
                    onClick={() => handlePageClick(1)}
                    disabled={currentPage === 1 || loading}
                    className="p-2 rounded-lg border border-gray-200 text-main1 hover:bg-main1/10 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    title="الصفحة الأولى"
                >
                    <FaAngleDoubleRight />
                </button>

                {/* Previous page button */}
                <button
                    onClick={() => handlePageClick(currentPage - 1)}
                    disabled={currentPage === 1 || loading}
                    className="p-2 rounded-lg border border-gray-200 text-main1 hover:bg-main1/10 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    title="السابق"
                >
                    <FaChevronRight />
                </button>

                {/* Page numbers */}
                <div className="flex items-center gap-1">
                    {pageNumbers.map((page, index) => (
                        page === '...' ? (
                            <span key={`ellipsis-${index}`} className="px-2 text-gray-400">
                                ...
                            </span>
                        ) : (
                            <button
                                key={page}
                                onClick={() => handlePageClick(page)}
                                disabled={loading}
                                className={`min-w-[40px] px-3 py-2 rounded-lg border text-sm font-medium transition-all ${currentPage === page
                                        ? 'bg-main1 text-white border-main1 shadow-md'
                                        : 'text-main1 border-gray-200 hover:bg-main1/10 disabled:opacity-50'
                                    }`}
                            >
                                {page}
                            </button>
                        )
                    ))}
                </div>

                {/* Next page button */}
                <button
                    onClick={() => handlePageClick(currentPage + 1)}
                    disabled={currentPage === totalPages || loading}
                    className="p-2 rounded-lg border border-gray-200 text-main1 hover:bg-main1/10 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    title="التالي"
                >
                    <FaChevronLeft />
                </button>

                {/* Last page button */}
                <button
                    onClick={() => handlePageClick(totalPages)}
                    disabled={currentPage === totalPages || loading}
                    className="p-2 rounded-lg border border-gray-200 text-main1 hover:bg-main1/10 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    title="الصفحة الأخيرة"
                >
                    <FaAngleDoubleLeft />
                </button>
            </div>
        </div>
    );
};

export default Pagination;
